// SEO-12 · /resources/marks-entry-a-second-examiner-can-check
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Claims registry D7 (approved wording): a paper time-overlapping another for the same
//     class-section is BLOCKED with an authorised override recorded; marks entry is limited to the
//     teacher's assigned sections; an optional second examiner must be a DIFFERENT person; editing
//     marks after submission INVALIDATES the moderation; absent is a distinct state, never a silent
//     zero. Invigilator rostering, room allocation and seating plans are NOT part of the module —
//     that boundary must be stated.
//   • CLAUDE.md exam-ops D2: `exams.marks_state` draft → in_progress → submitted → verified, the
//     maker-checker verify set {marks_first_by, entered_by, submitted_by}, and the auto-invalidation
//     that scrubs a stale submitted/verified attestation.
//   • Phase-5 re-evaluation (`core/reeval.py`): a re-check is the SANCTIONED exception to the
//     published-marks lock — one paper, fee-gated, maker-checker, and the result re-derives.
//   • Result classification (verdict/division/grace/compartment) is board CONFIGURATION resolved by
//     the 3-tier resolver, never a code branch (I-03); grace never buys a rank.
// NO claim about marking accuracy, no invented error rates, no automated marking claim.

const article = {
  slug: "marks-entry-a-second-examiner-can-check",
  title: "Marks entry a second examiner can actually check",
  seoTitle: "Marks Entry and Moderation in Schools | Vidyapeeth360",
  description:
    "Section-scoped entry, an optional second examiner, and what has to happen when a submitted mark is edited — the controls that make a result defensible.",
  topic: "Academics",
  topicKey: "academics",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "Nobody disputes a mark until the day somebody does. On that day the school needs to say who entered it, who checked it, and whether it changed after the checking — three facts that a spreadsheet cannot produce.",
  moduleLinks: ["digital-gradebook", "exam-scheduling", "result-publishing"],
  conversion: {
    to: "/contact",
    label: "Walk your examination workflow with us",
    note: "Bring a real term's papers; we will map entry, moderation and publication — and name what the module does not cover.",
  },
  sections: [
    {
      id: "the-day-it-matters",
      heading: "The workflow is fine until it is contested",
      blocks: [
        {
          t: "p",
          text: "Marks entry looks like data entry, so it gets treated like data entry: a shared sheet, a deadline, and a compile at the end. It works for years. Then one parent asks why their child's total changed between the provisional list and the report card, and the school discovers it has no way to answer.",
        },
        {
          t: "p",
          text: "The controls that make a result defensible are cheap to add before the term and impossible to add afterwards. There are four.",
        },
      ],
    },
    {
      id: "four-controls",
      heading: "Four controls, in order of value",
      blocks: [
        {
          t: "ol",
          items: [
            "**Entry is scoped to the person who taught the paper.** A teacher should open the sections they are assigned and no others. Wide access turns \"who entered this?\" into a question with no answer.",
            "**Absent is its own state.** A blank is not a zero, and a zero is not an absence. Collapsing them changes a pass into a fail and hides the case where a paper simply was not marked yet.",
            "**Submission is an act.** Saving marks is work in progress; submitting says the teacher is finished and is willing to be quoted. Until something is submitted there is nothing to check.",
            "**The checker is a different person.** A second examiner who can be the entering teacher is not a control, it is a checkbox. Whoever verifies must be somebody other than whoever authored — and, in practice, other than whoever last edited.",
          ],
        },
      ],
    },
    {
      id: "editing-after",
      heading: "What happens when a submitted mark is edited",
      blocks: [
        {
          t: "p",
          text: "This is the case that separates a real moderation workflow from a decorative one, and almost every school hits it: a paper is submitted, a moderator verifies it, and then somebody notices a transcription error and corrects it.",
        },
        {
          t: "p",
          text: "If the verification survives that edit, the school now has a mark that carries an attestation nobody actually gave for the value on record. The correct behaviour is unforgiving: an edit after submission invalidates the moderation, the paper returns to in-progress, and the stale attestation is removed rather than left in place. Somebody has to check again.",
        },
        {
          t: "note",
          title: "Once published, marks lock — with one sanctioned exception",
          text: "After results are published, marks should stop being editable, because a published result that quietly changes is worse than an error. The exception is a re-evaluation the family requested and the school accepted: one paper, re-marked by a named person, with the result re-derived from the revised mark. That is a workflow with a request, a fee decision and an approver — not an unlocked field.",
        },
      ],
    },
    {
      id: "scheduling",
      heading: "The schedule is part of the integrity story",
      blocks: [
        {
          t: "p",
          text: "Two papers for the same class-section at overlapping times is not a timetable inconvenience; it is a result that cannot be produced honestly, because a student physically could not sit both.",
        },
        {
          t: "p",
          text: "Block the overlap at the point of scheduling rather than discovering it in the exam hall. Allow an authorised override — schools genuinely do run split cohorts and special arrangements — and record that the override happened, so the exception is visible when the results are reviewed.",
        },
        {
          t: "p",
          text: "One boundary worth stating plainly, because buyers assume it: invigilator rostering, room allocation and seating plans are a different problem from marks integrity, and are not part of this module. If you need them, keep running them the way you do and do not let a demo imply otherwise.",
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Marks entry is limited to the class-sections a teacher is assigned, derived from the timetable and explicit teaching assignments — and that scope covers every marks-exposing view, not only the entry screen, so a sibling report cannot be used to read another section's marks.",
        },
        {
          t: "p",
          text: "Each paper carries a state: draft until marks are saved, then in progress, then submitted by the entering teacher, then verified by a moderator who must be a different person from the paper's original author, its last editor and its submitter. Second-examiner moderation is opt-in — a school that ignores it behaves exactly as before — and where it is used, editing a submitted or verified paper returns it to in progress and scrubs the previous attestation, so no un-reviewed mark ever carries somebody's verification.",
        },
        {
          t: "p",
          text: "Scheduling a paper that overlaps another for the same class-section is refused; an authorised person can schedule anyway, and that override is recorded. Absent is a distinct state that counts as a subject failure rather than a silent zero, and a paper that is not fully marked produces an incomplete result rather than a confident wrong one.",
        },
        {
          t: "p",
          text: "Verdicts, divisions and any grace rule are board configuration resolved per school, not a code branch — and a condoned borderline pass is capped at the lowest division and excluded from merit, so grace can never buy a rank. After publication, marks lock; a re-check is a request with an approver and a fee decision, after which the single revised mark re-derives the result. [Marks entry](/modules/digital-gradebook), [the exam schedule](/modules/exam-scheduling) and [result publishing](/modules/result-publishing) share one record, so a change in any of them is visible in the others.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
